import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { bold, num, tableRows } from "../../utils/xlsx";
import { AsOfFilter, cellRight, download, EmptyState, ExportButton, LoadingOverlay, money, REPORTS, sheetHeading, StatCard, todayIso } from "./common";
import LedgerVouchers from "./LedgerVouchers";

const signed = (row) => (row.side === "CR" ? -Number(row.balance) : Number(row.balance));

/** Cash in hand and the balance of every bank / OD account on a date. */
const CashBankBalances = () => {
  const [asOfDate, setAsOfDate] = useState(todayIso);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null);

  const load = async () => {
    if (!asOfDate) {
      toast.error("Select a date");
      return;
    }
    setLoading(true);
    try {
      setReport(await api.get(`${REPORTS}/cash-bank`, { asOfDate }));
    } catch (error) {
      toast.error(error.message || "Failed to load cash & bank balances");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allRows = report ? [...report.cashLedgers.map((r) => ({ ...r, type: "Cash" })), ...report.bankLedgers.map((r) => ({ ...r, type: r.group === "BANK_OD" ? "Bank OD / CC" : "Bank" }))] : [];

  const exportExcel = () => {
    const rows = [
      ...sheetHeading("Cash & Bank Balances", `As on ${report.asOfDate}`),
      ...tableRows(
        [
          { label: "Ledger", key: "ledgerName" },
          { label: "Type", key: "type" },
          { label: "Bank", key: "bankName" },
          { label: "Account No", key: "bankAccountNumber" },
          { label: "Balance", key: "balance", format: "money", total: false },
          { label: "Dr/Cr", key: "side" },
        ],
        allRows
      ),
      [],
      [bold("Cash in Hand"), "", "", "", bold(num(report.cashInHand))],
      [bold("Bank Accounts"), "", "", "", bold(num(report.bankBalance))],
      [bold("Bank OD / CC Used"), "", "", "", bold(num(report.bankOdOutstanding))],
      [bold("Cheques in Hand"), "", "", "", bold(num(report.chequesInHand))],
      [bold("Net Cash & Bank"), "", "", "", bold(num(report.totalLiquidFunds))],
    ];
    download(`Cash and Bank ${report.asOfDate}`, [{ name: "Cash & Bank", rows, widths: [34, 14, 26, 22, 16, 7] }]);
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Book balance of each cash and bank ledger: opening balance plus all receipts, less all payments, up to the date. Click a ledger to see its
        vouchers.
      </div>
      <div className="filters-bar">
        <AsOfFilter value={asOfDate} onChange={setAsOfDate} />
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={!report} />
      </div>

      {!report ? (
        <EmptyState>{loading ? "Loading..." : "No data loaded"}</EmptyState>
      ) : (
        <>
          <div className="stat-grid" style={{ marginTop: "16px" }}>
            <StatCard label="Cash in Hand" value={`₹${money(report.cashInHand)}`} tone={Number(report.cashInHand) < 0 ? "bad" : undefined} />
            <StatCard label="Bank Accounts" value={`₹${money(report.bankBalance)}`} tone={Number(report.bankBalance) < 0 ? "bad" : undefined} />
            <StatCard label="Bank OD / CC Used" value={`₹${money(report.bankOdOutstanding)}`} />
            <StatCard label="Cheques in Hand" value={`₹${money(report.chequesInHand)}`} hint="Collected, not yet cleared" />
            <StatCard label="Net Cash & Bank" value={`₹${money(report.totalLiquidFunds)}`} hint="Cash + Bank − OD" />
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Ledger</th>
                  <th>Type</th>
                  <th>Bank</th>
                  <th>Account No</th>
                  <th style={cellRight}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {allRows.map((row) => (
                  <tr key={row.ledgerId}>
                    <td style={{ textAlign: "left" }}>
                      <button
                        type="button"
                        onClick={() => setDrill(row)}
                        style={{ background: "none", border: "none", padding: 0, color: "var(--color-primary)", cursor: "pointer" }}
                      >
                        {row.ledgerName}
                      </button>
                    </td>
                    <td>{row.type}</td>
                    <td>{row.bankName || "-"}</td>
                    <td>{row.bankAccountNumber || "-"}</td>
                    <td style={{ ...cellRight, color: row.group !== "BANK_OD" && signed(row) < 0 ? "var(--color-danger)" : undefined }}>
                      {money(row.balance)} {row.side}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {drill ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setDrill(null)}>
          <div className="modal-box" style={{ maxWidth: "1100px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Ledger Vouchers — {drill.ledgerName}</h3>
              <button className="close-btn" onClick={() => setDrill(null)}>
                Close
              </button>
            </div>
            <LedgerVouchers initialLedgerId={drill.ledgerId} />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CashBankBalances;
